import React, { useEffect, useState } from "react";
import { Loader2, Save, Send, RefreshCw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { Card, Field, CopyButton, CodeBlock, inputCls, btnDark, btnLight } from "./atoms";

const EVENTS = [
  ["curated_trip.published", "El viaje pasa a publicado → la landing lo añade"],
  ["curated_trip.updated", "Cambia nombre, tagline, fechas, precios, cupo, foto o ciudades de un viaje publicado"],
  ["curated_trip.availability_changed", "Alguien retiene plazas, paga, o un hold caduca → plazas libres cambian"],
  ["curated_trip.sold_out", "Se agotan las plazas → la landing muestra «Completo»"],
  ["curated_trip.closed", "Cerrado a nuevas reservas (sigue visible)"],
  ["curated_trip.unpublished / .archived", "Vuelve a borrador o se archiva → la landing lo quita"],
  ["ping", "Evento de prueba desde este panel"],
];

const VERIFY = `// Node.js — comprobar la firma antes de procesar
const crypto = require("crypto");
const expected = "sha256=" + crypto.createHmac("sha256", WEBHOOK_SECRET)
  .update(rawBody).digest("hex");
if (expected !== req.headers["x-hub-signature-256"]) return res.status(401).end();
const { event, data } = JSON.parse(rawBody);   // data = mismo objeto que la API`;

export function WebhookCard({ settings, reload }) {
  const [url, setUrl] = useState(settings.webhook_url || "");
  const [enabled, setEnabled] = useState(!!settings.webhook_enabled);
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { setUrl(settings.webhook_url || ""); setEnabled(!!settings.webhook_enabled); }, [settings]);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/settings/hub/webhook", { webhook_url: url, webhook_enabled: enabled });
      toast.success(enabled && url ? "Webhook activo" : "Webhook guardado (desactivado)");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(null); }
  };

  const rotateSecret = async () => {
    if (!(await confirmAsync("La landing dejará de validar las firmas hasta que actualices el secreto allí. ¿Continuar?", { destructive: true, confirmLabel: "Rotar secreto" }))) return;
    setBusy("secret");
    try { await api.post("/settings/hub/webhook/secret"); setShowSecret(true); reload(); }
    catch (e) { toast.error("No se pudo rotar"); }
    finally { setBusy(null); }
  };

  const sendTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/hub/webhook/test");
      setTestResult(data);
      data.ok ? toast.success(`La landing respondió ${data.status_code}`) : toast.error(`Fallo: ${data.status_code || data.error}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar"); }
    finally { setBusy(null); }
  };

  return (
    <Card kicker="Otras integraciones" title="Webhook genérico firmado (HMAC)" testid="webhook-card">
      <p className="text-sm text-clay-700 -mt-2">
        Para cualquier otro sistema que quiera enterarse de los cambios (no hace falta para el hub, que usa la conexión de arriba).
        Cada vez que publicas, cierras o cambia la disponibilidad de un Curated Trip, enviamos un <code>POST</code> firmado
        con el mismo objeto que devuelve la API de lectura.
      </p>
      <Field label="URL que recibe los eventos" hint="Debe ser https y responder 2xx. Reintentamos 3 veces si falla la red o devuelve 5xx.">
        <input data-testid="webhook-url-input" className={inputCls} placeholder="https://tu-landing.com/api/espiritu-webhook" value={url} onChange={(e) => setUrl(e.target.value)}/>
      </Field>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="webhook-enabled-toggle" className="accent-terracotta"/>
        Enviar eventos automáticamente
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={save} disabled={busy === "save"} className={btnDark} data-testid="webhook-save-btn">
          {busy === "save" ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Guardar webhook
        </button>
        <button onClick={sendTest} disabled={!settings.webhook_url || busy === "test"} className={btnLight} data-testid="webhook-test-btn" title={!settings.webhook_url ? "Guarda primero la URL" : ""}>
          {busy === "test" ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>} Enviar evento de prueba
        </button>
      </div>
      {testResult && (
        <div data-testid="webhook-test-result" className={`text-[12px] p-3 border ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-terracotta/40 bg-terracotta/5 text-clay-900"}`}>
          {testResult.ok ? "✓" : "✗"} {testResult.event} → {testResult.status_code ?? "sin respuesta"} · {testResult.duration_ms} ms
          {testResult.error && <div className="mt-1 font-mono break-all">{testResult.error}</div>}
        </div>
      )}

      {settings.webhook_secret && (
        <div className="border border-clay-200 bg-clay-50 p-4">
          <div className="smallcaps">Secreto de firma (cabecera <code>X-Hub-Signature-256</code>)</div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <code className="font-mono text-[12px] bg-white border border-clay-200 px-2 py-1.5 break-all" data-testid="webhook-secret-value">
              {showSecret ? settings.webhook_secret : `${settings.webhook_secret.slice(0, 10)}${"•".repeat(24)}`}
            </code>
            <button onClick={() => setShowSecret((v) => !v)} className={btnLight} data-testid="webhook-secret-toggle">{showSecret ? <EyeOff size={12}/> : <Eye size={12}/>} {showSecret ? "Ocultar" : "Mostrar"}</button>
            <CopyButton text={settings.webhook_secret} testid="webhook-copy-secret"/>
            <button onClick={rotateSecret} disabled={busy === "secret"} className={btnLight} data-testid="webhook-rotate-secret-btn"><RefreshCw size={12}/> Rotar</button>
          </div>
        </div>
      )}

      <details className="text-sm">
        <summary className="cursor-pointer text-clay-800 hover:text-terracotta">Eventos y cómo verificar la firma</summary>
        <div className="mt-3 space-y-3">
          <table className="w-full text-[12px]">
            <tbody>
              {EVENTS.map(([ev, desc]) => (
                <tr key={ev} className="border-t border-clay-100"><td className="py-1.5 font-mono text-clay-800 pr-3 whitespace-nowrap">{ev}</td><td className="py-1.5 text-clay-700">{desc}</td></tr>
              ))}
            </tbody>
          </table>
          <CodeBlock>{`POST ${url || "https://tu-landing.com/…"}\nX-Hub-Event: curated_trip.availability_changed\nX-Hub-Delivery: whd_…\nX-Hub-Signature-256: sha256=<hmac del body>\n\n{ "id": "whd_…", "event": "curated_trip.availability_changed", "created_at": "…", "data": { …viaje… } }`}</CodeBlock>
          <CodeBlock>{VERIFY}</CodeBlock>
          <p className="text-[12px] text-clay-600">Trata cada evento como un «upsert» por <code>data.id</code>: la landing siempre recibe el estado completo del viaje, no un diff.</p>
        </div>
      </details>
    </Card>
  );
}
